import React, { useEffect, useState, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { DEFAULT_LOGO } from '../../config/brand';

const links = [
  { 
    to: '/admin', 
    label: 'Dashboard', 
    group: 'Overview',
    end: true, 
    module: 'dashboard',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7"/>
        <rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/>
        <rect x="3" y="14" width="7" height="7"/>
      </svg>
    )
  },
  { 
    to: '/admin/products', 
    label: 'Products', 
    group: 'Catalog',
    module: 'products',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
      </svg>
    )
  },
  { 
    to: '/admin/categories', 
    label: 'Categories', 
    group: 'Catalog',
    module: 'categories',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
      </svg>
    )
  },
  { 
    to: '/admin/variants', 
    label: 'Variants', 
    group: 'Catalog',
    module: 'products',
    action: 'manage_variants',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="4" y1="6" x2="20" y2="6"/>
        <line x1="4" y1="12" x2="20" y2="12"/>
        <line x1="4" y1="18" x2="20" y2="18"/>
        <circle cx="8" cy="6" r="2" fill="currentColor"/>
        <circle cx="16" cy="12" r="2" fill="currentColor"/>
        <circle cx="10" cy="18" r="2" fill="currentColor"/>
      </svg>
    )
  },
  { 
    to: '/admin/blogs', 
    label: 'Blog posts', 
    group: 'Catalog',
    module: 'blogs',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    )
  },
  { 
    to: '/admin/orders', 
    label: 'Orders', 
    group: 'Commerce',
    module: 'orders',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
      </svg>
    )
  },
  { 
    to: '/admin/transactions', 
    label: 'Transactions', 
    group: 'Commerce',
    module: 'transactions',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    )
  },
  {
    to: '/admin/promo-codes',
    label: 'Promo codes',
    group: 'Commerce',
    module: 'promo_codes',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
        <line x1="7" y1="7" x2="7.01" y2="7"/>
      </svg>
    )
  },
  { 
    to: '/admin/customers', 
    label: 'Customers', 
    group: 'Commerce',
    module: 'customers',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/>
        <path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    )
  },
  {
    to: '/admin/leads',
    label: 'Leads',
    group: 'Commerce',
    module: 'leads',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4v8z"/>
        <path d="M8 9h8M8 13h5"/>
      </svg>
    )
  },
  { 
    to: '/admin/profile', 
    label: 'Admin profile', 
    group: 'Team',
    module: 'dashboard',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    )
  },
  { 
    to: '/admin/roles', 
    label: 'Roles & permissions', 
    group: 'Team',
    module: 'users_roles',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    )
  },
  { 
    to: '/admin/admins', 
    label: 'Admin users', 
    group: 'Team',
    module: 'users_roles',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="8.5" cy="7" r="4"/>
        <polyline points="17 11 19 13 23 9"/>
      </svg>
    )
  },
  { 
    to: '/admin/shipping', 
    label: 'Shipping', 
    group: 'Configuration',
    module: 'shipping',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="3" width="15" height="13"/>
        <polygon points="16 8 20 8 23 11 23 16 16 16 8"/>
        <circle cx="5.5" cy="18.5" r="2.5"/>
        <circle cx="18.5" cy="18.5" r="2.5"/>
      </svg>
    )
  },
  { 
    to: '/admin/notifications', 
    label: 'Notifications', 
    group: 'Team',
    module: 'notifications',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 01-3.46 0"/>
      </svg>
    )
  },
  { 
    to: '/admin/settings', 
    label: 'Store settings', 
    group: 'Configuration',
    modules: ['settings_general', 'settings_smtp', 'settings_payment'],
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    )
  },
  {
    to: '/admin/system',
    label: 'System & Audit',
    group: 'Configuration',
    superAdminOnly: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2l8 4v6c0 5-3.4 8.7-8 10-4.6-1.3-8-5-8-10V6l8-4z"/>
        <path d="M9 12l2 2 4-5"/>
      </svg>
    )
  },
];

export default function AdminLayout() {
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Sidebar States: mobile drawer & desktop collapse
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('ca_admin_sidebar_collapsed') === 'true';
  });

  // Admin Workspace Theme State: 'light' (bright) | 'dark'
  const [adminTheme, setAdminTheme] = useState(() => {
    return localStorage.getItem('ca_admin_theme') || 'light';
  });

  const [newLeadCount, setNewLeadCount] = useState(0);

  // Topbar Dropdown states
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [quickActionOpen, setQuickActionOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Live Notifications State
  const [notifications, setNotifications] = useState([]);
  const [notifFilter, setNotifFilter] = useState('all');
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  const notifRef = useRef(null);
  const profileRef = useRef(null);
  const quickActionRef = useRef(null);
  const searchInputRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);

  // Toggle Admin Dark/Bright Mode
  const toggleAdminTheme = () => {
    setAdminTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('ca_admin_theme', next);
      toast.success(next === 'dark' ? 'Dark Mode Enabled' : 'Bright Mode Enabled');
      return next;
    });
  };

  // Full Screen State & Listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        )
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (
      !document.fullscreenElement &&
      !document.webkitFullscreenElement &&
      !document.mozFullScreenElement &&
      !document.msFullscreenElement
    ) {
      const docEl = document.documentElement;
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen().catch(() => {});
      } else if (docEl.webkitRequestFullscreen) {
        docEl.webkitRequestFullscreen();
      } else if (docEl.mozRequestFullScreen) {
        docEl.mozRequestFullScreen();
      } else if (docEl.msRequestFullscreen) {
        docEl.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // Toggle sidebar collapse on desktop & persist preference
  const toggleSidebarCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('ca_admin_sidebar_collapsed', String(next));
      return next;
    });
  };

  // Close dropdowns on route changes
  useEffect(() => {
    setSidebarOpen(false);
    setNotifOpen(false);
    setProfileOpen(false);
    setQuickActionOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (quickActionRef.current && !quickActionRef.current.contains(e.target)) setQuickActionOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard Shortcuts: Cmd/Ctrl + K (search), Cmd/Ctrl + B (sidebar toggle), and Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggleSidebarCollapse();
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setNotifOpen(false);
        setProfileOpen(false);
        setQuickActionOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  const handleLogout = () => {
    logout();
    toast.success('Admin logged out successfully');
    navigate('/admin/login');
  };

  const roleObj = user?.role;
  const isSuperAdmin = user?.accountType === 'admin' && (roleObj?.isSystem === true || !roleObj);

  const canAccess = (item) => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (isSuperAdmin) return true;
    if (!roleObj || !roleObj.permissions) return false;
    const moduleKeys = item.modules || [item.module];
    return moduleKeys.some((moduleKey) => {
      const permission = roleObj.permissions.find((entry) => entry.module === moduleKey);
      return permission?.actions?.includes(item.action || 'view');
    });
  };
  const visibleLinks = links.filter(canAccess);
  const canViewLeads = visibleLinks.some((item) => item.module === 'leads');

  // Leads count fetcher
  useEffect(() => {
    if (!canViewLeads) return undefined;
    const fetchCount = () => api.get('/leads', { params: { status: 'new', page: 1, limit: 1 } })
      .then((res) => setNewLeadCount(res.data.newCount || 0))
      .catch(() => {});
    fetchCount();
    const intervalId = window.setInterval(fetchCount, 30000);
    window.addEventListener('leads:changed', fetchCount);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('leads:changed', fetchCount);
    };
  }, [canViewLeads]);

  // Notifications fetcher
  const fetchNotifications = () => {
    setLoadingNotifs(true);
    api.get('/notifications/my')
      .then((res) => setNotifications(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingNotifs(false));
  };

  useEffect(() => {
    fetchNotifications();
    const timer = setInterval(fetchNotifications, 25000);
    window.addEventListener('notifications:refresh', fetchNotifications);
    return () => {
      clearInterval(timer);
      window.removeEventListener('notifications:refresh', fetchNotifications);
    };
  }, []);

  const unreadNotifs = notifications.filter((n) => !n.isRead);
  const unreadCount = unreadNotifs.length;

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Could not mark all as read');
    }
  };

  const markSingleRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)));
    } catch (_) {}
  };

  const clearAllNotifs = async () => {
    try {
      await api.patch('/notifications/clear-all');
      setNotifications([]);
      toast.success('All notifications cleared');
    } catch {
      toast.error('Could not clear notifications');
    }
  };

  const displayedNotifs = notifFilter === 'unread' ? unreadNotifs : notifications;

  const getNotifIcon = (type) => {
    switch (type) {
      case 'order':
        return (
          <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 flex items-center justify-center shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
          </div>
        );
      case 'stock':
        return (
          <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 flex items-center justify-center shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
        );
      case 'lead':
        return (
          <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center justify-center shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8z"/></svg>
          </div>
        );
      case 'promotion':
        return (
          <div className="w-8 h-8 rounded-lg bg-purple-50 border border-purple-200 text-purple-800 flex items-center justify-center shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] border border-[#E8DFD8] text-[#C49A45] flex items-center justify-center shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
          </div>
        );
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(dateStr).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  const filteredSearchLinks = visibleLinks.filter((l) =>
    l.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.group.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groups = ['Overview', 'Catalog', 'Commerce', 'Team', 'Configuration'];
  const currentItem = visibleLinks
    .filter((item) => item.end ? location.pathname === item.to : location.pathname.startsWith(item.to))
    .sort((a, b) => b.to.length - a.to.length)[0];

  return (
    <div className={`min-h-screen flex text-[#2B1810] transition-colors duration-200 ${adminTheme === 'dark' ? 'admin-dark bg-[#140E0B]' : 'bg-[#FAF8F5]'}`}>
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-xs z-40 lg:hidden"
          aria-label="Close admin navigation"
        />
      )}

      {/* Sticky Left Sidebar Navigation with Thin Scrollbar & Collapsible Width */}
      <aside
        className={`fixed lg:sticky inset-y-0 left-0 z-50 bg-[#211A17] text-[#E8DFD8] flex flex-col shrink-0 border-r border-[#3D332E] top-0 h-screen transition-all duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${sidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-60'} w-60`}
      >
        {/* Brand Header */}
        <div className="p-3.5 border-b border-[#3D251A] flex items-center justify-between gap-2 shrink-0">
          {!sidebarCollapsed ? (
            <div className="min-w-0 flex-1">
              <span className="block rounded-md bg-[#F7F3EC] px-2 py-1.5 shadow-xs">
                <img src={resolveMedia(settings?.logo || DEFAULT_LOGO)} alt={settings?.siteName || 'Chandana Aura'} className="h-9 w-full object-contain" />
              </span>
              <span className="mt-1 block text-center text-[7.5px] font-bold uppercase tracking-widest text-[#C49A45]">
                Super Admin Console
              </span>
            </div>
          ) : (
            <div className="w-full flex flex-col items-center justify-center">
              <span className="w-10 h-10 rounded-lg bg-[#F7F3EC] flex items-center justify-center font-heading font-bold text-lg text-[#C49A45] shadow-xs border border-[#C49A45]/30">
                CA
              </span>
            </div>
          )}

          {/* Desktop Toggle Button inside Sidebar */}
          <button
            type="button"
            onClick={toggleSidebarCollapse}
            className="hidden lg:flex w-7 h-7 rounded-md bg-[#2B231F] hover:bg-[#3D332E] text-[#CFC3BA] hover:text-white items-center justify-center transition-colors border border-[#3D332E] shrink-0"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label="Toggle sidebar collapse"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
            >
              <path d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>

          {/* Mobile Close Drawer Button */}
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden ml-auto w-8 h-8 flex items-center justify-center text-[#E8DFD8] hover:text-white"
            aria-label="Close menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {/* Sidebar Links with Ultra-Thin Scrollbar */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4 thin-scrollbar">
          {groups.map((group) => {
            const groupLinks = visibleLinks.filter((item) => item.group === group);
            if (!groupLinks.length) return null;
            return (
              <div key={group}>
                {!sidebarCollapsed ? (
                  <p className="mb-1.5 px-2 text-[7.5px] font-bold uppercase tracking-wider text-[#8F7E73]">{group}</p>
                ) : (
                  <div className="my-2 border-t border-[#332823]" />
                )}
                <div className="space-y-1">
                  {groupLinks.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      title={sidebarCollapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        `group relative flex items-center gap-3 px-2.5 py-2 text-[10px] font-semibold transition-all rounded-lg ${
                          sidebarCollapsed ? 'justify-center' : ''
                        } ${
                          isActive
                            ? 'bg-[#C49A45] text-white font-bold shadow-xs'
                            : 'text-[#CFC3BA] hover:bg-[#2B231F] hover:text-white'
                        }`
                      }
                    >
                      <span className="shrink-0 relative">
                        {item.icon}
                        {sidebarCollapsed && item.module === 'leads' && newLeadCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#C49A45] rounded-full ring-2 ring-[#211A17]" />
                        )}
                        {sidebarCollapsed && item.module === 'notifications' && unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-600 rounded-full ring-2 ring-[#211A17] animate-pulse" />
                        )}
                      </span>
                      
                      {!sidebarCollapsed && (
                        <>
                          <span className="truncate">{item.label}</span>
                          {item.module === 'leads' && newLeadCount > 0 && (
                            <span className="ml-auto min-w-5 bg-[#C49A45] px-1.5 py-0.5 text-center text-[8px] font-bold text-white rounded-full">
                              {newLeadCount > 99 ? '99+' : newLeadCount}
                            </span>
                          )}
                          {item.module === 'notifications' && unreadCount > 0 && (
                            <span className="ml-auto min-w-5 bg-red-600 px-1.5 py-0.5 text-center text-[8px] font-bold text-white rounded-full animate-pulse">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </>
                      )}

                      {/* Tooltip on Collapsed Mode */}
                      {sidebarCollapsed && (
                        <div className="absolute left-full ml-3 px-2.5 py-1 bg-[#1A120C] text-white text-xs font-bold whitespace-nowrap rounded-md shadow-xl border border-[#3D332E] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-50 hidden lg:block">
                          {item.label}
                        </div>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Sidebar Footer User Card */}
        <div className="p-3 border-t border-[#3D251A] bg-[#1A120C]/90 space-y-2 shrink-0">
          <Link
            to="/admin/profile"
            title={sidebarCollapsed ? `${user?.name || 'Super Admin'} - View Profile` : undefined}
            className={`flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-[#2B231F] transition-colors group relative ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C49A45] to-[#8C6B28] text-white flex items-center justify-center font-bold text-xs shadow-sm shrink-0 border border-[#C49A45]/40">
              {user?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            
            {!sidebarCollapsed && (
              <div className="truncate flex-1 min-w-0">
                <span className="block text-xs font-bold text-[#FAF5EE] truncate group-hover:text-[#C49A45] transition-colors">
                  {user?.name || 'Super Admin'}
                </span>
                <span className="block text-[9px] text-[#C49A45] font-semibold truncate">
                  {isSuperAdmin ? 'Super Admin' : user?.role?.name || 'Admin'}
                </span>
              </div>
            )}

            {!sidebarCollapsed && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[#8F7E73] group-hover:text-white transition-colors"><path d="M9 18l6-6-6-6"/></svg>
            )}

            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-2.5 py-1 bg-[#1A120C] text-white text-xs font-bold whitespace-nowrap rounded-md shadow-xl border border-[#3D332E] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-50 hidden lg:block">
                {user?.name || 'Super Admin'} · Profile
              </div>
            )}
          </Link>

          {!sidebarCollapsed && (
            <div className="flex items-center gap-2 pt-1">
              <Link
                to="/"
                target="_blank"
                className="flex-1 text-center py-1.5 bg-[#3D251A] hover:bg-[#4D3022] text-[#E8DFD8] text-[9.5px] font-bold uppercase rounded border border-[#543628] transition-colors inline-flex items-center justify-center gap-1"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/></svg>
                <span>Store</span>
              </Link>
              <button
                onClick={handleLogout}
                className="px-2.5 py-1.5 bg-red-900/60 hover:bg-red-800 text-red-200 text-[9.5px] font-bold uppercase rounded border border-red-700/50 transition-colors"
                title="Logout"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Body */}
      <div className="flex-1 flex flex-col min-w-0 min-h-screen">
        {/* Top Header Bar with Profile, Notifications & Dark/Bright Mode Toggle */}
        <header className="bg-white border-b border-[#DED6CF] px-4 sm:px-6 py-2 sm:py-2.5 min-h-[50px] flex items-center justify-between sticky top-0 z-30 gap-3 shadow-xs">
          {/* Left Side: Mobile Drawer Button + Workspace Title */}
          <div className="flex items-center gap-3">
            {/* Mobile Hamburger Drawer Button */}
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center text-[#2B1810] hover:bg-[#FAF8F5] rounded-lg transition-colors"
              aria-label="Open admin navigation"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[8px] font-bold uppercase tracking-wider text-[#9D772D]">Admin Workspace</span>
                <span className="text-[9px] font-bold px-1.5 py-0.2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hidden sm:inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live Store
                </span>
              </div>
              <p className="text-xs sm:text-sm font-bold text-[#241914] truncate max-w-[180px] sm:max-w-none">
                {currentItem?.label || 'Super Admin Dashboard'}
              </p>
            </div>
          </div>

          {/* Right Side: Dark Mode Toggle + Search + Quick Action + Notification Bell + Profile Dropdown */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* 1. Dark Mode / Bright Mode Toggle Icon */}
            <button
              type="button"
              onClick={toggleAdminTheme}
              className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${
                adminTheme === 'dark'
                  ? 'border-[#C49A45] bg-[#2A1F1A] text-[#C49A45] hover:bg-[#342721] shadow-xs'
                  : 'border-[#D9D0C8] bg-white text-[#2B1810] hover:bg-[#FAF8F5] hover:border-[#C49A45]/60'
              }`}
              title={adminTheme === 'dark' ? 'Switch to Bright Mode' : 'Switch to Dark Mode'}
              aria-label="Toggle Dashboard Dark or Bright Mode"
            >
              {adminTheme === 'dark' ? (
                // Radiant Sun Icon (for Bright Mode)
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                // Crescent Moon Icon (for Dark Mode)
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>

            {/* 2. Full Screen Toggle Icon */}
            <button
              type="button"
              onClick={toggleFullscreen}
              className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${
                isFullscreen
                  ? 'border-[#C49A45] bg-[#C49A45]/15 text-[#C49A45] shadow-xs'
                  : adminTheme === 'dark'
                  ? 'border-[#3D332E] bg-[#2A1F1A] text-[#C49A45] hover:bg-[#342721]'
                  : 'border-[#D9D0C8] bg-white text-[#2B1810] hover:bg-[#FAF8F5] hover:border-[#C49A45]/60'
              }`}
              title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
              aria-label="Toggle Full Screen"
            >
              {isFullscreen ? (
                // Exit Full Screen Icon
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              ) : (
                // Enter Full Screen Icon
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              )}
            </button>

            {/* 3. Global Quick Search Button */}
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setSearchOpen(true);
              }}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#D9D0C8] rounded-lg text-xs text-[#8A796C] transition-colors"
              title="Search admin navigation"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <span>Quick Search...</span>
            </button>

            {/* 3. Quick Actions Dropdown (+ Create) */}
            <div className="relative" ref={quickActionRef}>
              <button
                type="button"
                onClick={() => {
                  setQuickActionOpen(!quickActionOpen);
                  setNotifOpen(false);
                  setProfileOpen(false);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-[#C49A45] hover:bg-[#B08736] text-white text-xs font-bold uppercase rounded-lg shadow-xs transition-all hover:scale-105"
                title="Quick actions"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                <span className="hidden sm:inline">Create</span>
              </button>

              {quickActionOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-[#E8DFD8] rounded-xl shadow-xl py-2 z-50 animate-in fade-in duration-150">
                  <div className="px-3 py-1.5 border-b border-[#E8DFD8] text-[9px] font-bold uppercase text-[#8A796C] tracking-wider">
                    Quick Shortcuts
                  </div>
                  <Link
                    to="/admin/products/new"
                    onClick={() => setQuickActionOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                    <span>Add New Product</span>
                  </Link>
                  <Link
                    to="/admin/blogs/new"
                    onClick={() => setQuickActionOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg>
                    <span>Write Blog Post</span>
                  </Link>
                  <Link
                    to="/admin/promo-codes"
                    onClick={() => setQuickActionOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                    <span>Create Promo Code</span>
                  </Link>
                  <Link
                    to="/admin/notifications"
                    onClick={() => setQuickActionOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 11l18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>
                    <span>Send Broadcast Alert</span>
                  </Link>
                  {isSuperAdmin && (
                    <Link
                      to="/admin/admins"
                      onClick={() => setQuickActionOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors border-t border-[#E8DFD8]"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>
                      <span>Invite Admin User</span>
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* 4. Live Notification Bell & Dropdown */}
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                onClick={() => {
                  setNotifOpen(!notifOpen);
                  setProfileOpen(false);
                  setQuickActionOpen(false);
                }}
                className={`relative w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${
                  notifOpen
                    ? 'border-[#C49A45] bg-[#FAF8F5] text-[#C49A45]'
                    : 'border-[#D9D0C8] bg-white text-[#2B1810] hover:bg-[#FAF8F5]'
                }`}
                title="Notifications"
                aria-label="Admin Notifications"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 01-3.46 0"/>
                </svg>

                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 bg-red-600 text-white font-bold text-[9px] rounded-full flex items-center justify-center shadow-xs animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown Panel with Thin Scrollbar */}
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-[#E8DFD8] rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in duration-150">
                  {/* Dropdown Header */}
                  <div className="p-4 bg-[#FAF8F5] border-b border-[#E8DFD8] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-sm text-[#2B1810]">Notifications</h3>
                      {unreadCount > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">
                          {unreadCount} new
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          onClick={markAllRead}
                          className="text-[10px] font-bold uppercase text-[#C49A45] hover:underline"
                        >
                          Mark all read
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={clearAllNotifs}
                        className="text-[10px] font-bold uppercase text-red-700 hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Filter Tabs */}
                  <div className="flex border-b border-[#E8DFD8] bg-white text-xs font-semibold text-[#8A796C]">
                    <button
                      type="button"
                      onClick={() => setNotifFilter('all')}
                      className={`flex-1 py-2 text-center border-b-2 transition-colors ${
                        notifFilter === 'all'
                          ? 'border-[#C49A45] text-[#C49A45] font-bold bg-[#FAF8F5]'
                          : 'border-transparent hover:text-[#2B1810]'
                      }`}
                    >
                      All ({notifications.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setNotifFilter('unread')}
                      className={`flex-1 py-2 text-center border-b-2 transition-colors ${
                        notifFilter === 'unread'
                          ? 'border-[#C49A45] text-[#C49A45] font-bold bg-[#FAF8F5]'
                          : 'border-transparent hover:text-[#2B1810]'
                      }`}
                    >
                      Unread ({unreadCount})
                    </button>
                  </div>

                  {/* Notification Items List with Thin Scrollbar */}
                  <div className="max-h-72 overflow-y-auto divide-y divide-[#E8DFD8] thin-scrollbar">
                    {loadingNotifs && notifications.length === 0 ? (
                      <div className="p-6 text-center text-xs text-[#8A796C]">Loading alerts...</div>
                    ) : displayedNotifs.length === 0 ? (
                      <div className="p-8 text-center space-y-2">
                        <div className="w-10 h-10 rounded-full bg-[#FAF8F5] border border-[#E8DFD8] flex items-center justify-center mx-auto text-[#C49A45]">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        </div>
                        <p className="text-xs font-bold text-[#2B1810]">All caught up</p>
                        <p className="text-[10px] text-[#8A796C]">No notifications to display.</p>
                      </div>
                    ) : (
                      displayedNotifs.slice(0, 15).map((n) => (
                        <div
                          key={n._id}
                          onClick={() => {
                            if (!n.isRead) markSingleRead(n._id);
                            if (n.type === 'order') navigate('/admin/orders');
                            else if (n.type === 'lead') navigate('/admin/leads');
                            setNotifOpen(false);
                          }}
                          className={`p-3.5 flex items-start gap-3 hover:bg-[#FAF8F5] transition-colors cursor-pointer relative ${
                            !n.isRead ? 'bg-amber-50/40' : 'bg-white'
                          }`}
                        >
                          {getNotifIcon(n.type)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className={`text-xs truncate ${!n.isRead ? 'font-bold text-[#2B1810]' : 'font-medium text-[#4A3B32]'}`}>
                                {n.title}
                              </p>
                              <span className="text-[9px] text-[#8A796C] shrink-0 font-medium">{formatTime(n.createdAt)}</span>
                            </div>
                            <p className="text-[11px] text-[#8A796C] line-clamp-2 mt-0.5 leading-relaxed">
                              {n.message}
                            </p>
                          </div>
                          {!n.isRead && (
                            <span className="w-2 h-2 rounded-full bg-[#C49A45] shrink-0 self-center" />
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Dropdown Footer */}
                  <div className="p-3 bg-[#FAF8F5] border-t border-[#E8DFD8] flex items-center justify-between text-xs">
                    <Link
                      to="/admin/notifications"
                      onClick={() => setNotifOpen(false)}
                      className="font-bold text-[#C49A45] hover:underline flex items-center gap-1 text-[11px]"
                    >
                      <span>View All Broadcasts</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14m-7-7 7 7-7 7"/></svg>
                    </Link>
                    <button
                      type="button"
                      onClick={fetchNotifications}
                      className="text-[10px] text-[#8A796C] hover:text-[#2B1810] font-semibold"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 5. Super Admin Profile Menu Dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                onClick={() => {
                  setProfileOpen(!profileOpen);
                  setNotifOpen(false);
                  setQuickActionOpen(false);
                }}
                className={`flex items-center gap-2 p-1 sm:px-2.5 sm:py-1 rounded-xl border transition-all ${
                  profileOpen
                    ? 'border-[#C49A45] bg-[#FAF8F5]'
                    : 'border-[#D9D0C8] bg-white hover:border-[#C49A45]/60 hover:bg-[#FAF8F5]'
                }`}
                aria-label="Admin Profile Menu"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[#C49A45] to-[#8C6B28] text-white flex items-center justify-center font-bold text-xs shadow-xs border border-[#C49A45]/30 shrink-0">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </div>
                <div className="hidden sm:block text-left">
                  <span className="block text-xs font-bold text-[#241914] leading-tight truncate max-w-[100px]">
                    {user?.name?.split(' ')[0] || 'Admin'}
                  </span>
                  <span className="block text-[9px] font-semibold text-[#9D772D] leading-tight">
                    {isSuperAdmin ? 'Super Admin' : user?.role?.name || 'Staff'}
                  </span>
                </div>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-[#8A796C] transition-transform ${profileOpen ? 'rotate-180' : ''}`}><path d="M6 9l6 6 6-6"/></svg>
              </button>

              {/* Profile Dropdown Menu */}
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white border border-[#E8DFD8] rounded-2xl shadow-2xl py-2 z-50 animate-in fade-in duration-150">
                  {/* User Profile Header Card */}
                  <div className="p-4 border-b border-[#E8DFD8] bg-[#FAF8F5]/80 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#C49A45] to-[#8C6B28] text-white flex items-center justify-center font-heading font-bold text-base shadow-sm shrink-0 border-2 border-white">
                      {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading font-bold text-sm text-[#2B1810] truncate">
                        {user?.name || 'Administrator'}
                      </p>
                      <p className="text-[11px] text-[#8A796C] truncate">{user?.email || 'admin@chandanaaura.com'}</p>
                      <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase bg-[#C49A45]/15 text-[#8C6B28]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C49A45]" />
                        {isSuperAdmin ? 'Super Admin' : user?.role?.name || 'Admin'}
                      </span>
                    </div>
                  </div>

                  {/* Navigation Links */}
                  <div className="py-1">
                    <Link
                      to="/admin/profile"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] shrink-0">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                      <div>
                        <p className="font-bold leading-tight">My Profile & Security</p>
                        <p className="text-[10px] text-[#8A796C] font-normal">Edit details & change password</p>
                      </div>
                    </Link>

                    <Link
                      to="/admin/settings"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] shrink-0">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      </div>
                      <div>
                        <p className="font-bold leading-tight">Store & Payment Settings</p>
                        <p className="text-[10px] text-[#8A796C] font-normal">Razorpay, Stripe & PayPal keys</p>
                      </div>
                    </Link>

                    {isSuperAdmin && (
                      <Link
                        to="/admin/roles"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] shrink-0">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        </div>
                        <div>
                          <p className="font-bold leading-tight">Roles & Permissions</p>
                          <p className="text-[10px] text-[#8A796C] font-normal">Manage team access rules</p>
                        </div>
                      </Link>
                    )}

                    <Link
                      to="/admin/system"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] shrink-0">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                      </div>
                      <div>
                        <p className="font-bold leading-tight">System & Audit Log</p>
                        <p className="text-[10px] text-[#8A796C] font-normal">Server runtime & diagnostics</p>
                      </div>
                    </Link>

                    <Link
                      to="/"
                      target="_blank"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-[#2B1810] hover:bg-[#FAF8F5] hover:text-[#C49A45] transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] shrink-0">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/></svg>
                      </div>
                      <div>
                        <p className="font-bold leading-tight">Open Storefront</p>
                        <p className="text-[10px] text-[#8A796C] font-normal">View customer-facing shop</p>
                      </div>
                    </Link>
                  </div>

                  {/* Logout Button */}
                  <div className="p-2 border-t border-[#E8DFD8] bg-[#FAF8F5]">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold uppercase rounded-lg border border-rose-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Global Quick Search Modal with Click Outside to Dismiss */}
        {searchOpen && (
          <div
            className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/60 backdrop-blur-xs animate-fadeIn"
            onClick={() => setSearchOpen(false)}
          >
            <div
              className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-[#E8DFD8] overflow-hidden animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-[#E8DFD8] flex items-center gap-3 bg-[#FAF8F5]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C49A45" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type to search admin modules (e.g. Products, Orders, Settings)..."
                  className="w-full bg-transparent text-sm font-semibold text-[#2B1810] outline-none placeholder-[#8A796C]"
                />
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="p-1 rounded text-[#8A796C] hover:text-[#2B1810] text-xs font-mono bg-white border border-[#D9D0C8] cursor-pointer"
                >
                  ESC
                </button>
              </div>

              <div className="max-h-72 overflow-y-auto p-2 divide-y divide-[#E8DFD8] thin-scrollbar">
                {filteredSearchLinks.length === 0 ? (
                  <div className="p-6 text-center text-xs text-[#8A796C]">No matching admin sections found.</div>
                ) : (
                  filteredSearchLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setSearchOpen(false)}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-[#FAF8F5] transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-[#FAF8F5] border border-[#E8DFD8] group-hover:border-[#C49A45] flex items-center justify-center text-[#2B1810] group-hover:text-[#C49A45] transition-colors">
                          {item.icon}
                        </span>
                        <div>
                          <p className="text-xs font-bold text-[#2B1810] group-hover:text-[#C49A45] transition-colors">{item.label}</p>
                          <p className="text-[10px] text-[#8A796C]">{item.group}</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-[#8A796C] font-mono group-hover:text-[#C49A45]">Jump →</span>
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dynamic Admin View */}
        <main className="flex-1 p-3 sm:p-6 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
